import { AxiosInstance } from "axios";
import { MarkdownEditorApi, ToolbarDropdown } from "@/text/MarkdownEditor";
import { isNotBlank } from "@/utils/strings";
import { makeAutoObservable } from "mobx";
import {
  cancelled,
  failure,
  isCancelled,
  isOk,
  isPending,
  ok,
  pending,
  Result,
} from "@/common/common";
import { DropdownMenuItems, DropdownMenuItemType } from "@/components-v2/Dropdown";
import { Language } from "@/i18n/languages";
import { TFunction, TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { StringFieldModel } from "@/common/commonForms";
import { compact, sortBy } from "lodash";
import {
  autotextCategories,
  autotextCategoryIcons,
  autotextCategoryLabels,
  dataRequestTypeLabels,
  DataRequest,
  SelectionAwareDataSource,
  initDataRequest,
} from "./autotext";
import { HttpEvent } from "@/events/eventsDomain";

export enum PromptType {
  CUSTOM = "CUSTOM",
  FREETEXT_DESCRIPTION = "FREETEXT_DESCRIPTION",
  COMPLETION_MESSAGE_TO_SHAREHOLDERS = "COMPLETION_MESSAGE_TO_SHAREHOLDERS",
  COMPLETION_MESSAGE_TO_BOARD_MEMBERS = "COMPLETION_MESSAGE_TO_BOARD_MEMBERS",
}

export enum CompletionLength {
  COMPLEMENTARY = "COMPLEMENTARY",
  CONCISE = "CONCISE",
}

export enum CompletionStyle {
  FORMAL = "FORMAL",
  NEUTRAL = "NEUTRAL",
  ENGAGING = "ENGAGING",
}

export enum AIAction {
  COMPLETE = "COMPLETE",
  EXPAND = "EXPAND",
  CONCISE = "CONCISE",
  CUSTOM = "CUSTOM",
}

export const aiActionLabels: Record<AIAction, TMessage> = {
  [AIAction.COMPLETE]: { key: "ai.actions.COMPLETE" },
  [AIAction.EXPAND]: { key: "ai.actions.EXPAND" },
  [AIAction.CONCISE]: { key: "ai.actions.CONCISE" },
  [AIAction.CUSTOM]: { key: "ai.actions.CUSTOM" },
};

export const completionStyleLabels: { [key in CompletionStyle]: TMessageKey } = {
  [CompletionStyle.FORMAL]: "ai.settings.styles.FORMAL",
  [CompletionStyle.ENGAGING]: "ai.settings.styles.ENGAGING",
  [CompletionStyle.NEUTRAL]: "ai.settings.styles.NEUTRAL",
};

export const completionLengthLabels: { [key in CompletionLength]: TMessageKey } = {
  [CompletionLength.COMPLEMENTARY]: "ai.length.COMPLEMENTARY",
  [CompletionLength.CONCISE]: "ai.length.CONCISE",
};

export function toProgressMessage(
  t: TFunction,
  aiAction: AIAction,
  length: CompletionLength,
  style: CompletionStyle,
  payload: string,
) {
  return t("ai.improve.text.progress", {
    action: `{${t(aiActionLabels[aiAction])}}`,
    text: `{${payload}}`,
    length: `{${t(completionLengthLabels[length])}}`,
    style: `{${t(completionStyleLabels[style])}}`,
  });
}

function autotextOptions({
  t,
  onSelect,
  filter,
  onFilterChange,
  options,
}: {
  t: TFunction;
  options: {
    includeShareCapitalIncrease?: boolean;
    includeCommittedCapital?: boolean;
    includeReductionOfShareCapital?: boolean;
    includeIncorporation?: boolean;
    includeMerger?: boolean;
    includeOptions?: boolean;
  };
  filter: string;
  onFilterChange: (s: string) => void;
  onSelect: (v: DataRequest["type"]) => void;
}): ToolbarDropdown {
  const preppedFilter = prepFilter(filter);
  return {
    icon: "list-plus",
    label: { key: "ai.improve.autotext" },
    tooltip: { key: "ai.improve.autotext" },
    items: autotextCategories(options).flatMap<DropdownMenuItemType>(([category, variables]) => {
      const preppedVariables = variables.flatMap<DropdownMenuItemType>((v) => {
        const label = t(dataRequestTypeLabels[v]);
        return filterVariable(preppedFilter, v, label)
          ? [
              {
                type: "item",
                label: t(dataRequestTypeLabels[v]),
                categoryTag: v,
                onSelect: () => onSelect(v),
              },
            ]
          : [];
      });
      return preppedVariables.length
        ? [
            {
              type: "label",
              icon: autotextCategoryIcons[category],
              label: t(autotextCategoryLabels[category]),
            },
            ...sortBy(preppedVariables, "label"),
          ]
        : [];
    }),
    filterPlaceholder: { key: "ai.autotext.filter.placeholder" },
    filter,
    onFilterChange,
  };
}

const escapePattern = (s: string): string => s.replaceAll(/([()[\].\\])/g, "\\1");

export const prepFilter = (filter: string): ((s: string) => boolean) => {
  if (!filter.trim()) {
    return () => true;
  }
  const rs = filter.split(" ").map((s) => new RegExp(escapePattern(s.trim()), "i"));
  return (s: string) => rs.every((r) => r.test(s));
};

export const filterVariable = (filter: (s: string) => boolean, v: string, label: string): boolean =>
  filter(v) || filter(label);

function actionOptions(onSelect: (action: AIAction) => void): ToolbarDropdown {
  return {
    icon: "sparkle",
    label: { key: "ai.improve.text" },
    tooltip: { key: "ai.improve.text" },
    items: Object.values(AIAction).map((action) => ({
      type: "item",
      label: aiActionLabels[action],
      onSelect: () => onSelect(action),
    })),
  };
}

export function settingsOptions(
  completionStyle: CompletionStyle,
  completionLength: CompletionLength,
  onSelect: (option: CompletionStyle | CompletionLength) => void,
): ToolbarDropdown {
  const styles: DropdownMenuItems = Object.values(CompletionStyle).map((option) => ({
    type: "radio-item",
    label: { key: completionStyleLabels[CompletionStyle[option]] },
    isSelected: option === completionStyle,
    onSelect: () => onSelect(option),
  }));
  const lengths: DropdownMenuItems = Object.values(CompletionLength).map((option) => ({
    type: "radio-item",
    label: { key: completionLengthLabels[CompletionLength[option]] },
    isSelected: option === completionLength,
    onSelect: () => onSelect(option),
  }));
  return {
    icon: "gear",
    tooltip: { key: "ai.settings" },
    items: [
      { type: "label", label: { key: "ai.settings" } },
      { type: "separator" },
      { type: "label", label: { key: "ai.length" } },
      ...lengths,
      { type: "separator" },
      { type: "label", label: { key: "ai.style" } },
      ...styles,
    ],
  };
}

export const aiApi = {
  translateMessage: ({
    inputText,
    from,
    to,
    ...params
  }: {
    inputText: string;
    from: Language;
    to: Language;
    onSuccess: HttpEvent<string>["onSuccess"];
    onFailure: HttpEvent<string>["onFailure"];
  }): HttpEvent<string> => ({
    type: "http",
    method: "post",
    url: "/ai/translate",
    data: { inputText, from, to },
    ...params,
  }),
};

export class AIClient {
  readonly http: AxiosInstance;
  readonly orgNo?: string;

  constructor(http: AxiosInstance, orgNo?: string) {
    this.http = http;
    this.orgNo = orgNo;
  }

  async improveMessage(
    inputText: string,
    promptType: PromptType,
    action: AIAction,
    mode: CompletionStyle,
    language: Language,
  ): Promise<string> {
    const res = await this.http.post(`/ai${this.orgNo ? "/" + this.orgNo : ""}/complete`, {
      promptType,
      action,
      mode,
      inputText,
      language,
      temperature: "medium",
    });
    return res.data;
  }

  async translateMessage(
    text: string,
    fromLanguage: Language,
    toLanguage: Language,
  ): Promise<string> {
    const res = await this.http.post(`/ai/translate`, {
      inputText: text,
      from: fromLanguage,
      to: toLanguage,
    });
    return res.data;
  }
}

export class AIModel {
  aiAction: AIAction = AIAction.COMPLETE;
  style: CompletionStyle = CompletionStyle.NEUTRAL;
  length: CompletionLength = CompletionLength.COMPLEMENTARY;
  showCustomDialog = false;
  customAction: StringFieldModel;
  progressMessage: string = "";
  readonly aiClient: AIClient;
  improveMessageResult: Result<string, TMessage> | undefined;
  translateMessageResult: Result<string, TMessage> | undefined;

  dataProvider?: SelectionAwareDataSource;
  editorApi?: MarkdownEditorApi;
  autotextFilter = "";

  constructor(aiClient: AIClient, dataProvider?: SelectionAwareDataSource) {
    this.aiClient = aiClient;
    this.dataProvider = dataProvider;
    this.customAction = new StringFieldModel();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get showImproveMessageProgressBar(): boolean {
    return isPending(this.improveMessageResult) || isOk(this.improveMessageResult);
  }

  get showTranslateProgressBar(): boolean {
    return isPending(this.translateMessageResult);
  }

  set<T extends keyof this>(key: T, val: this[T]) {
    this[key] = val;
  }

  setOption(option: CompletionStyle | CompletionLength) {
    if (option in CompletionStyle) {
      this.style = option as CompletionStyle;
    } else {
      this.length = option as CompletionLength;
    }
  }

  hideCustomDialog() {
    this.showCustomDialog = false;
  }

  setEditorApi(editorApi: MarkdownEditorApi) {
    this.editorApi = editorApi;
  }

  getCustomOptions(t: TFunction, message: string, language: Language, promptType: PromptType) {
    return compact([
      this.dataProvider &&
        autotextOptions({
          t,
          options: this.dataProvider,
          onSelect: (v) => {
            this.editorApi?.insertVariable(initDataRequest(v));
            this.set("autotextFilter", "");
          },
          filter: this.autotextFilter,
          onFilterChange: (s) => this.set("autotextFilter", s),
        }),
      actionOptions((action) =>
        this.improveMessageWithAction(action, message, language, promptType, t),
      ),
      settingsOptions(this.style, this.length, this.setOption),
    ]);
  }

  get progress(): string {
    return isOk(this.improveMessageResult) ? this.improveMessageResult.data : this.progressMessage;
  }

  submitCustomQuery(language: Language) {
    this.showCustomDialog = false;
    this.progressMessage = this.customAction.inputValue;
    this.improveMessage(
      this.aiAction,
      this.style,
      "",
      language,
      PromptType.CUSTOM,
      this.customAction.inputValue,
    );
  }

  improveMessageWithAction(
    aiAction: AIAction,
    payload: string,
    language: Language,
    promptType: PromptType,
    t: TFunction,
  ) {
    this.aiAction = aiAction;
    if (aiAction === AIAction.CUSTOM) {
      this.customAction.setAndCommitInputValue(
        toProgressMessage(t, AIAction.COMPLETE, this.length, this.style, payload),
      );
      this.showCustomDialog = true;
    } else {
      this.progressMessage = toProgressMessage(t, aiAction, this.length, this.style, payload);
      this.improveMessage(aiAction, this.style, payload, language, promptType);
    }
  }

  improveMessage(
    aiAction: AIAction,
    style: CompletionStyle,
    inputText: string,
    language: Language,
    promptType: PromptType,
    customText?: string,
  ) {
    let payload = inputText;
    if (aiAction === AIAction.CUSTOM) {
      payload = customText!;
    }
    if (payload.split(/\s+/).length < 3) {
      this.improveMessageResult = failure({ key: "ai.error.minimumLength" });
      return;
    }
    this.improveMessageResult = pending();
    this.aiClient
      .improveMessage(payload, promptType, aiAction, style, language)
      .then((res) =>
        !isCancelled(this.improveMessageResult) ? (this.improveMessageResult = ok(res)) : undefined,
      )
      .catch(() => (this.improveMessageResult = failure({ key: "ai.notAvailable" })));
  }

  *translateMessage(
    inputText: string,
    fromLanguage: Language,
    toLanguage: Language,
    callback: (result: string) => void,
  ) {
    this.translateMessageResult = pending();
    try {
      const input = inputText.trim();
      if (isNotBlank(input)) {
        const result: string = yield this.aiClient.translateMessage(
          input,
          fromLanguage,
          toLanguage,
        );
        if (!isCancelled(this.translateMessageResult)) {
          this.translateMessageResult = ok(result);
          callback(result);
        }
      } else {
        this.translateMessageResult = ok(input);
        callback(input);
      }
    } catch (e) {
      this.translateMessageResult = failure({ key: "ai.notAvailable" });
    }
  }

  cancelImproveMessageResult() {
    this.improveMessageResult = cancelled();
  }
}
